import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../config/permissions';
import adminNotificationService from '../services/adminNotificationService';

const REFETCH_EVENT = 'admin-notification-count-refetch';

export function refetchAdminNotificationCount() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(REFETCH_EVENT));
  }
}

export default function useAdminNotificationCount() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView = isSuperAdmin || hasPermission(PERMISSIONS.ADMIN_NOTIFICATIONS_VIEW);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!canView) {
      setCount(0);
      return;
    }
    setLoading(true);
    try {
      const unreadCount = await adminNotificationService.getUnreadCount();
      setCount(unreadCount);
    } catch {
      // Keep last known count on transient errors.
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    if (!canView) return undefined;

    const handleRefetch = () => {
      fetchCount();
    };

    window.addEventListener('focus', handleRefetch);
    window.addEventListener(REFETCH_EVENT, handleRefetch);
    return () => {
      window.removeEventListener('focus', handleRefetch);
      window.removeEventListener(REFETCH_EVENT, handleRefetch);
    };
  }, [canView, fetchCount]);

  return { count, loading, canView, refetch: fetchCount };
}
